'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { useTranslation } from '@/i18n/client'
import { FglHeader } from '@/components/layout/fgl-header'
import { GliderBackdrop, LivingThermal, Reveal } from '@/components/award'
import { GaforMap } from '@/components/weather/gafor-map'
import { getRegionById } from '@/data/gafor-regions'

interface CurrentWeather {
  temperature: number
  windSpeed: number
  windDirection: number
  windGust: number
  cloudCover: number
  precipitation: number
  pressure: number
  dewPoint: number
  sunshine: number
  icon: string
}

interface HourlyForecast {
  time: string
  temperature: number
  windSpeed: number
  windDirection: number
  cloudCover: number
  precipitation: number
  thermalIndex: number
  icon: string
}

interface ThermalIndex {
  flatland: number
  slope: number
  overall: number
  cloudBase: number
  thermalStrength: number
  bestHours: string[]
  recommendation: string
}

interface SunTimes {
  sunrise: string
  sunset: string
  civilDawn: string
  civilDusk: string
  vfrStart: string
  vfrEnd: string
  daylightHours: number
}

interface WeatherData {
  current: CurrentWeather
  forecast: HourlyForecast[]
  thermalIndex: ThermalIndex
  sun?: SunTimes
  forecastDays?: number
  source: string
  station?: string
  updatedAt: string
  timezone?: string  // IANA z.B. "Europe/Berlin"
}

interface ThermalForecastHotspot {
  id: string
  lat: number
  lon: number
  score: number
  avg_climb_rate: number
  avg_alt_gain: number
  todayProbability: number
  weatherBoost: boolean
  windMatch: boolean
  reason: string
  bestTimeRange: string
}

interface ThermalForecastData {
  hotspots: ThermalForecastHotspot[]
  weather: {
    thermalIndex: number
    windSpeed: number
    windDirection: number
    cloudBase: number
    thermalStrength: number
  }
  bestTimeWindow: string
}

interface FieldRecommendation {
  id: string
  name: string
  lat: number
  lon: number
  distanceKm: number
  terrain: 'slope' | 'flatland'
  altitude_m: number | null
  country: string | null
  preferred_wind_dir: number | null
  thermalIndex: number
  slopeIndex: number
  windSpeed: number
  windDirection: number
  windMatch: 'perfect' | 'good' | 'poor' | 'opposite' | null
  windMatchAngle: number | null
  score: number
  reason: string
}

// Wind direction to arrow rotation
function windArrowDeg(deg: number): number {
  return (deg + 180) % 360
}

// Windrichtung in Grad → Himmelsrichtung (16-teilig)
function degToCompass(deg: number): string {
  const dirs = ['N','NNO','NO','ONO','O','OSO','SO','SSO','S','SSW','SW','WSW','W','WNW','NW','NNW']
  return dirs[Math.round(deg / 22.5) % 16]
}

// Weather icon mapping to emoji
function weatherEmoji(icon: string): string {
  const map: Record<string, string> = {
    'clear-day': '\u2600\uFE0F',
    'clear-night': '\uD83C\uDF19',
    'partly-cloudy-day': '\u26C5',
    'partly-cloudy-night': '\uD83C\uDF24\uFE0F',
    'cloudy': '\u2601\uFE0F',
    'fog': '\uD83C\uDF2B\uFE0F',
    'wind': '\uD83C\uDF2C\uFE0F',
    'rain': '\uD83C\uDF27\uFE0F',
    'sleet': '\uD83C\uDF28\uFE0F',
    'snow': '\u2744\uFE0F',
    'hail': '\uD83C\uDF28\uFE0F',
    'thunderstorm': '\u26C8\uFE0F',
  }
  return map[icon] || '\u2601\uFE0F'
}

// Parse "HH:MM" time string to minutes since midnight
function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + (m || 0)
}

// Check if hour (0-23) is within VFR window (civilDawn to civilDusk)
function isInVfrWindow(hour: number, sun?: SunTimes): boolean {
  if (!sun?.civilDawn || !sun?.civilDusk) {
    // Fallback: Mitteleuropa April ca. 05:30-20:30 UTC
    return hour >= 5 && hour <= 21
  }
  const minuteOfHour = hour * 60
  const dawn = parseTimeToMinutes(sun.civilDawn)
  const dusk = parseTimeToMinutes(sun.civilDusk)
  return minuteOfHour >= dawn && minuteOfHour <= dusk
}

// Color for probability (>70% green, 40-70% yellow, <40% gray)
function probColor(p: number): string {
  if (p >= 0.7) return '#22c55e'
  if (p >= 0.4) return '#eab308'
  return '#6b7280'
}

// Color for thermal index (0=red, 5=yellow, 10=green)
function indexColor(value: number): string {
  if (value >= 8) return '#22c55e'
  if (value >= 6) return '#84cc16'
  if (value >= 4) return '#eab308'
  if (value >= 2) return '#f97316'
  return '#ef4444'
}

// 2026-06-19 Kontrast-Sweep: deep-Variante fuer Text-Kontexte auf hellen Themes
function indexColorText(value: number): string {
  if (value >= 8) return '#15803d'
  if (value >= 6) return '#3f6212'
  if (value >= 4) return '#854d0e'
  if (value >= 2) return '#9a3412'
  return '#b91c1c'
}

// Background gradient for index card (dezent, max ~8% Deckkraft)
function indexBg(value: number): string {
  const c = indexColor(value)
  return `linear-gradient(135deg, ${c}14, ${c}05)`
}

// Einheiten-Konvertierung
function toF(c: number): number { return c * 9 / 5 + 32 }
function toMph(kmh: number): number { return kmh * 0.621371 }
function toInHg(hpa: number): number { return hpa * 0.02953 }
function toFt(m: number): number { return m * 3.28084 }

/**
 * R100.57 (2026-05-30): Konvertiert eine UTC-Zeit-String (z.B. "06:15") in
 * die lokale Zeit der angegebenen IANA-Zeitzone (z.B. "Europe/Berlin").
 * Liefert einen formatierten String "HH:MM" zurück. Bei Fehler den
 * Original-String zurückgeben.
 */
function formatUtcAsLocal(utcTime: string, timezone: string | undefined): string {
  if (!timezone || timezone === 'UTC') return utcTime
  const match = utcTime.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return utcTime
  try {
    const today = new Date()
    const utc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), Number(match[1]), Number(match[2])))
    return utc.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: timezone })
  } catch {
    return utcTime
  }
}

export default function WeatherPage() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const isLoggedIn = !!session?.user
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [location, setLocation] = useState<{ lat: number; lon: number } | null>(null)
  const [locationName, setLocationName] = useState<string>('')
  const [geoPermission, setGeoPermission] = useState<'prompt' | 'granted' | 'denied'>('prompt')
  const [units, setUnits] = useState<'metric' | 'imperial'>('metric')
  const [recommendations, setRecommendations] = useState<FieldRecommendation[]>([])
  const [recsLoading, setRecsLoading] = useState(false)
  const [recsRadius, setRecsRadius] = useState(50)
  const [recsLimit, setRecsLimit] = useState<number>(10)
  const [recsTerrainFilter, setRecsTerrainFilter] = useState<'all' | 'flatland' | 'slope'>('all')
  const [thermalForecast, setThermalForecast] = useState<ThermalForecastData | null>(null)
  const [thermalForecastLoading, setThermalForecastLoading] = useState(false)
  const [soaringForecast, setSoaringForecast] = useState<any>(null)
  const [allGaforData, setAllGaforData] = useState<Map<number, any>>(new Map())
  const [forecastDayOffset, setForecastDayOffset] = useState(0) // 0=today, 1=tomorrow, 2=day after
  const [potential, setPotential] = useState<any>(null)

  const fetchRecommendations = useCallback(async (lat: number, lon: number, radius: number) => {
    setRecsLoading(true)
    try {
      const res = await fetch(`/api/weather/recommendations?lat=${lat}&lon=${lon}&radius=${radius}`)
      if (!res.ok) return
      const data = await res.json()
      setRecommendations(data.recommendations || [])
    } catch {
      // Silently fail — recommendations are secondary
    } finally {
      setRecsLoading(false)
    }
  }, [])

  const fetchThermalForecast = useCallback(async (lat: number, lon: number) => {
    setThermalForecastLoading(true)
    try {
      const res = await fetch(`/api/thermal-atlas/forecast?lat=${lat}&lon=${lon}&radius=50`)
      if (res.ok) {
        const data = await res.json()
        setThermalForecast(data)
      }
    } catch {
      // Silently fail — thermal forecast is secondary
    } finally {
      setThermalForecastLoading(false)
    }
  }, [])

  const fetchWeather = useCallback(async (lat: number, lon: number) => {
    setLoading(true)
    setError(null)
    try {
      // Konsistenz-Vorteil: gleiche Public-API wie Coaching-App + Drittentwickler.
      // Antwort-Schema ist 'WeatherForecastResponse' (siehe /api-docs).
      const res = await fetch(`/api/public/weather/forecast?lat=${lat}&lon=${lon}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const pub = await res.json()
      // Adapter: Public-Schema → legacy WeatherData (rueckwaerts-kompatibel mit
      // den Renderern auf dieser Seite; Public-Schema ist Superset).
      const data: WeatherData = {
        current: pub.now,
        forecast: pub.hourly ?? [],
        // Tages-Soaring-Index aus dem Public-Endpoint uebernehmen — gleiche Skala
        thermalIndex: {
          flatland: pub.soaring_index?.thermal ?? 0,
          slope: pub.soaring_index?.slope ?? 0,
          overall: pub.soaring_index?.combined ?? 0,
          // Felder die das Public-Schema nicht direkt liefert: aus Daily/Hourly ableiten
          cloudBase: 0,
          thermalStrength: 0,
          bestHours: (pub.soaring_index?.best_hours ?? []).map((iso: string) => {
            const d = new Date(iso)
            return `${String(d.getHours()).padStart(2, '0')}:00`
          }),
          recommendation: pub.soaring_index?.recommendation ?? '',
        },
        sun: pub.sun,
        forecastDays: 3,
        source: pub.source,
        station: pub.station ?? undefined,
        updatedAt: pub.generated_at,
        timezone: pub.timezone ?? undefined,
      }
      setWeather(data)
      if (data.station) setLocationName(data.station)
      // Fetch recommendations + thermal forecast after weather loads
      fetchRecommendations(lat, lon, recsRadius)
      fetchThermalForecast(lat, lon)
    } catch (err: any) {
      setError(err.message || t('weather.noData'))
    } finally {
      setLoading(false)
    }
  }, [t, fetchRecommendations, fetchThermalForecast, recsRadius])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation({ lat: 50.17, lon: 8.27 })
      setLocationName('Taunus')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude })
        setGeoPermission('granted')
      },
      () => {
        setGeoPermission('denied')
        setLocation({ lat: 50.17, lon: 8.27 })
        setLocationName('Taunus (Fallback)')
      },
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }, [])

  // Einmalig beim Laden: URL-Parameter oder Standort ermitteln
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlLat = params.get('lat')
    const urlLon = params.get('lon')
    if (urlLat && urlLon) {
      const lat = parseFloat(urlLat)
      const lon = parseFloat(urlLon)
      if (!isNaN(lat) && !isNaN(lon)) {
        setLocation({ lat, lon })
        setGeoPermission('granted')
        return
      }
    }
    requestLocation()
  }, [requestLocation])

  // Einmalig: ALLE 57 GAFOR-Regionen laden für die Hotspot-Karte
  useEffect(() => {
    const DWD_IDS = [10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,31,32,33,34,35,36,37,38,39,41,42,43,44,45,46,47,50,51,52,53,54,55,56,57,58,61,62,63,64,71,72,73,74,75,76,81,82,83,84]
    const map = new Map<number, any>()
    // Lade in Batches von 10 parallel
    const loadBatch = async (ids: number[]) => {
      await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(`/api/soaring-forecast?regionId=${id}`, { signal: AbortSignal.timeout(20000) })
          if (res.ok) {
            const data = await res.json()
            const maxClimb = data.summary?.maxClimbRate
            const maxPfd = data.summary?.pfdDaily
            const hourly = data.hourly || []
            // Beste Thermik-Stunde (höchstes Steigen) für Wind-Anzeige im Popup
            const bestHour = hourly
              .filter((h: any) => h.avgClimbRate != null && h.avgClimbRate > 0)
              .sort((a: any, b: any) => (b.avgClimbRate || 0) - (a.avgClimbRate || 0))[0]
            // Durchschnittlicher Wind über alle Thermik-Stunden für Index-Berechnung
            const thermalHours = hourly.filter((h: any) => h.avgClimbRate != null && h.avgClimbRate > 0)
            const avgWind = thermalHours.length > 0
              ? thermalHours.reduce((s: number, h: any) => s + (h.windSpeed || 0), 0) / thermalHours.length
              : bestHour?.windSpeed || 0
            const maxGust = thermalHours.length > 0
              ? Math.max(...thermalHours.map((h: any) => h.windGust || 0))
              : bestHour?.windGust || 0
            map.set(id, {
              climbRate: maxClimb,
              pfd: maxPfd,
              windSpeed: bestHour?.windSpeed ?? hourly.find((h: any) => h.windSpeed)?.windSpeed,
              windGust: bestHour?.windGust ?? hourly.find((h: any) => h.windGust)?.windGust,
              windDirection: bestHour?.windDirection ?? hourly.find((h: any) => h.windDirection)?.windDirection,
              // Thermik-Index: DWD Steigen / 3.5 × 10, Wind-gedämpft (Durchschnitt Thermik-Stunden)
              thermalIndex: (() => {
                if (maxClimb == null) return 0
                let ti = Math.min(10, (maxClimb / 3.5) * 10)
                const w = avgWind
                const g = maxGust
                let wf = 1.0
                if (w > 5) wf = Math.max(0.5, 1.0 - ((w - 5) / 20) * 0.25)
                if (g > 25) wf -= Math.min(0.15, (g - 25) / 50)
                wf = Math.max(0.1, wf)
                return Math.round(ti * wf * 10) / 10
              })(),
              slopeIndex: (() => { const w = bestHour?.windSpeed || hourly.find((h: any) => h.windSpeed)?.windSpeed || 0; return Math.min(10, Math.round(Math.max(0, w < 5 ? w * 0.2 : w < 15 ? 1 + (w - 5) * 0.4 : 5 + (w - 15) * 0.25) * 10) / 10) })(),
            })
          }
        } catch { /* timeout/error — skip */ }
      }))
    }
    // 2026-05-15: 57 Regionen × 1 Request = leicht über 100/min Limit. Batch
    // auf 5 reduziert + 250ms Pause zwischen Batches → max ~12 Req/sec, glättet
    // den Burst genug für die neue weather-Kategorie (500/min).
    ;(async () => {
      for (let i = 0; i < DWD_IDS.length; i += 5) {
        await loadBatch(DWD_IDS.slice(i, i + 5))
        if (i + 5 < DWD_IDS.length) {
          await new Promise(resolve => setTimeout(resolve, 250))
        }
      }
      setAllGaforData(new Map(map))
    })()
  }, [])

  // ZENTRAL: Bei jeder Standort-Änderung ALLES neu laden
  useEffect(() => {
    if (!location) return
    const { lat, lon } = location

    // 1. Wetter laden
    fetchWeather(lat, lon)

    // 2. DWD Segelflugvorhersage (nur Deutschland)
    setSoaringForecast(null)
    fetch(`/api/soaring-forecast?lat=${lat}&lon=${lon}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setSoaringForecast(d))
      .catch(() => setSoaringForecast(null))

    // 2b. VLOS-Tagesvorhersage ("Was schaffst du heute?")
    setPotential(null)
    fetch(`/api/weather/potential?lat=${lat}&lon=${lon}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setPotential(d))
      .catch(() => setPotential(null))

    // 3. Empfehlungen + Thermik-Hotspots
    fetchRecommendations(lat, lon, recsRadius)
    fetchThermalForecast(lat, lon)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  // Get today's forecast hours
  const todayForecast = weather?.forecast.filter((h) => {
    const d = new Date(h.time)
    const now = new Date()
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth()
  }) || []

  // 72h forecast grouped by day offset (0=today, 1=tomorrow, 2=day after)
  const forecastByDay = (offset: number) => {
    const target = new Date()
    target.setDate(target.getDate() + offset)
    const targetDate = target.getDate()
    const targetMonth = target.getMonth()
    return (weather?.forecast || []).filter((h) => {
      const d = new Date(h.time)
      return d.getDate() === targetDate && d.getMonth() === targetMonth
    })
  }

  const selectedDayForecast = forecastByDay(forecastDayOffset)

  // Day labels for tabs
  const dayLabels = [
    t('weather.today'),
    t('weather.tomorrow'),
    t('weather.dayAfter'),
  ]

  const dayDates = [0, 1, 2].map(offset => {
    const d = new Date()
    d.setDate(d.getDate() + offset)
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })
  })

  return (
    <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
      <FglHeader />
      <GliderBackdrop />

      {/* HERO — Award-Pattern wie auf Features/FAQ/Donate */}
      <section className="relative pt-[100px] pb-8 md:pt-[120px] md:pb-10 px-4 sm:px-6 overflow-hidden">
        <LivingThermal />
        <div className="absolute inset-0 award-noise pointer-events-none" />
        <div className="relative z-10 max-w-[1200px] mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="award-eyebrow">{t('weather.eyebrowIndex')}</span>
              <span className="h-px w-12" style={{ background: 'var(--award-line-hi)' }} />
              <span className="award-label">{t('weather.subtitle')}</span>
            </div>
          </Reveal>
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={1}>
                <h1 className="award-h-mega" style={{ marginBottom: 0 }}>
                  Modellflug-<span className="award-grad-text">Wetter.</span>
                </h1>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-5">
              <Reveal delay={2}>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                  Thermik- und Hangwind-Index für deinen Standort. DWD (DACH) ·
                  NOAA (USA) · JMA (Japan) · MET Norway weltweit. 72-Stunden-Vorhersage
                  mit Empfehlung der besten Flugzeiten.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <main className="pb-16 px-4 max-w-[1200px] mx-auto">
        {/* Location Header */}
        <section className="mb-8">
          {/* Standort-Eingabe Container — gleiche Breite wie Index-Card */}
          <div className="rounded-[20px] border border-[var(--accent)]/30 p-4 sm:p-5 flex items-center gap-3 flex-wrap"
            style={{ background: 'rgba(242,201,78,0.04)', boxShadow: '0 0 20px rgba(242,201,78,0.08), inset 0 1px 0 rgba(242,201,78,0.1)' }}>
            {locationName && (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--bg-card)] border border-[var(--accent)]/40 text-[13px] font-black text-[var(--accent)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {locationName}
              </span>
            )}
            <button
              onClick={requestLocation}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--accent)]/15 border border-[var(--accent)]/40 text-[12px] font-black text-[var(--accent)] hover:bg-[var(--accent)]/25 transition-colors cursor-pointer"
              data-tip="Standort per GPS ermitteln"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/></svg>
              {t('weather.locationPermission')}
            </button>
            <form
              className="inline-flex items-center gap-1.5 flex-1 min-w-[200px]"
              onSubmit={async (e) => {
                e.preventDefault()
                const input = (e.currentTarget.elements.namedItem('locationSearch') as HTMLInputElement)?.value?.trim()
                if (!input) return
                try {
                  const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=json&limit=1`)
                  const data = await res.json()
                  if (data?.[0]) {
                    setLocation({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) })
                    setLocationName(data[0].display_name?.split(',').slice(0, 2).join(', ') || input)
                  }
                } catch { /* ignore */ }
              }}
            >
              <input
                name="locationSearch"
                type="text"
                placeholder="Ort eingeben..."
                className="flex-1 px-4 py-2 rounded-full bg-[var(--bg-card)] border-2 border-[var(--accent)]/30 text-[13px] text-[var(--text)] font-bold focus:border-[var(--accent)]/60 focus:outline-none focus:shadow-[0_0_12px_rgba(242,201,78,0.15)] transition-all"
                data-tip="Ort eingeben und mit Enter oder Suchen bestätigen"
              />
              <button
                type="submit"
                className="px-5 py-2 rounded-full bg-[var(--accent)]/20 border-2 border-[var(--accent)]/50 text-[13px] font-black text-[var(--accent)] hover:bg-[var(--accent)]/30 hover:shadow-[0_0_12px_rgba(242,201,78,0.2)] transition-all cursor-pointer"
                data-tip="Standort suchen"
              >
                Suchen
              </button>
            </form>
            {location && (
              <a href={`/app/thermal-atlas?lat=${location.lat.toFixed(4)}&lon=${location.lon.toFixed(4)}`}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--accent)]/15 border border-[var(--accent)]/40 text-[12px] font-black text-[var(--accent)] hover:bg-[var(--accent)]/25 transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v8l4 4M12 22a10 10 0 1 0 0-20"/></svg>
                Thermik-Atlas
              </a>
            )}
          </div>
        </section>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[14px] font-bold text-[var(--muted)]">{t('weather.loading')}</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="rounded-[20px] border border-red-500/30 bg-red-500/5 p-6 text-center">
            <p className="text-[14px] font-bold text-red-400">{error}</p>
            <button onClick={requestLocation} className="mt-3 btn-primary !px-4 !py-2 !text-[12px]">
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Weather Content */}
        {weather && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* DWD-basierte Indizes überschreiben MET Norway wenn verfügbar (zuverlässiger) */}
            {(() => {
              if (soaringForecast?.summary?.maxClimbRate != null) {
                const dwdClimb = soaringForecast.summary.maxClimbRate
                const windH = soaringForecast.hourly?.find((h: any) => h.windSpeed)
                const windSpd = windH?.windSpeed || 0
                const gustSpd = windH?.windGust || windSpd

                // Thermik-Index Basis: 3.5 m/s Steigen = Index 10
                let dwdThermal = Math.min(10, (dwdClimb / 3.5) * 10)

                // Wind-Dämpfung auf Thermik:
                // ≤5 km/h: 100% (kein Einfluss)
                // 5-25 km/h: linear 100% → 50%
                // >25 km/h: unter 50% (weiter fallend)
                let windFactor = 1.0
                if (windSpd > 5) {
                  windFactor = Math.max(0.5, 1.0 - ((windSpd - 5) / 20) * 0.25) // 5→100%, 25→75%
                }
                // Böen-Dämpfung: Böen >15 km/h reduzieren zusätzlich
                if (gustSpd > 25) {
                  const gustPenalty = Math.min(0.15, (gustSpd - 25) / 50) // max 15% Abzug durch Böen
                  windFactor -= gustPenalty
                }
                windFactor = Math.max(0.1, windFactor) // Minimum 10%

                dwdThermal = Math.round(dwdThermal * windFactor * 10) / 10

                // Hang-Index: Wind ist der Treiber
                // <5→schwach, 10→3, 15→5, 20→7, 25→8, 30→9, 35+→10
                const dwdSlope = Math.min(10, Math.round(Math.max(0,
                  windSpd < 5 ? windSpd * 0.2 :
                  windSpd < 15 ? 1 + (windSpd - 5) * 0.4 :
                  5 + (windSpd - 15) * 0.25
                ) * 10) / 10)

                weather.thermalIndex.flatland = dwdThermal
                weather.thermalIndex.slope = dwdSlope
                weather.thermalIndex.overall = Math.min(10, Math.round((Math.max(dwdThermal, dwdSlope) + Math.min(dwdThermal, dwdSlope) * 0.25) * 10) / 10)

                // Thermikstärke aus DWD überschreiben
                weather.thermalIndex.thermalStrength = dwdClimb

                // Recommendation-Text aus DWD-Index (nicht MET Norway)
                const oi = weather.thermalIndex.overall
                const start = soaringForecast.summary?.thermalStart || soaringForecast.areaAverage?.thermalStart
                const end = soaringForecast.summary?.thermalEnd || soaringForecast.areaAverage?.thermalEnd
                const timeInfo = start && end ? ` Thermik ${start}–${end} UTC.` : ''
                weather.thermalIndex.recommendation = oi >= 8 ? `Ausgezeichneter Flugtag!${timeInfo} Starke Thermik erwartet.`
                  : oi >= 6 ? `Guter Thermiktag!${timeInfo} Thermik gut nutzbar.`
                  : oi >= 4 ? `Ordentlicher Flugtag.${timeInfo} Moderate Thermik möglich.`
                  : oi >= 2 ? `Eingeschränkt fliegbar.${timeInfo} Wenig Thermik.`
                  : `Heute ungünstig.${timeInfo} Kaum Thermik, evtl. Hangwind prüfen.`

                // Beste Flugzeiten aus DWD
                if (start && end) {
                  weather.thermalIndex.bestHours = [start, end]
                }
              }
              return null
            })()}

            {/* ══════ Thermik-Index Card (spans 2 cols on lg) ══════ */}
            <div className="lg:col-span-2 rounded-[20px] border border-[var(--border)] overflow-hidden"
              style={{ background: 'var(--bg-card)' }}>
              {/* Gold accent top bar */}
              <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${indexColor(weather.thermalIndex.overall)}00, ${indexColor(weather.thermalIndex.overall)}, ${indexColor(weather.thermalIndex.overall)}00)` }} />

              <div className="p-6 sm:p-8">
                {/* Overall Index */}
                <div className="flex items-center gap-6 mb-6">
                  <div className="text-center shrink-0" data-tip="Modellflug-Index 0–10: max(Thermik, Hang) + Synergie-Bonus">
                    <div className="text-[64px] sm:text-[80px] font-black leading-none"
                      style={{ color: indexColor(weather.thermalIndex.overall), textShadow: `0 0 30px ${indexColor(weather.thermalIndex.overall)}30` }}>
                      {weather.thermalIndex.overall.toFixed(1)}
                    </div>
                    <div className="text-[10px] font-black tracking-[.12em] uppercase text-[var(--muted)] mt-0.5">/ 10</div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-[16px] sm:text-[20px] font-black text-[var(--accent)] mb-1.5 tracking-[.02em]">
                      {t('weather.thermalIndex')}
                    </h2>
                    <p className="text-[12px] text-[var(--muted)] font-medium leading-relaxed">
                      {weather.thermalIndex.recommendation}
                    </p>
                  </div>
                </div>

                {/* Gauges Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Thermik Gauge */}
                  <div className="rounded-[12px] p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}
                    data-tip={t('weather.tipThermalGauge')}>
                    <div className="text-[9px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-2">{t('weather.thermalIndexLabel')}</div>
                    <div className="relative w-14 h-14 mx-auto">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke="var(--border)" strokeWidth="2.5" opacity="0.4" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke={indexColor(weather.thermalIndex.flatland)} strokeWidth="2.5"
                          strokeDasharray={`${weather.thermalIndex.flatland * 10}, 100`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[15px] font-black"
                        style={{ color: indexColor(weather.thermalIndex.flatland) }}>
                        {weather.thermalIndex.flatland.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* Hangwind Gauge */}
                  <div className="rounded-[12px] p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}
                    data-tip={t('weather.tipSlopeGauge')}>
                    <div className="text-[9px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-2">{t('weather.slopeIndex')}</div>
                    <div className="relative w-14 h-14 mx-auto">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke="var(--border)" strokeWidth="2.5" opacity="0.4" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke={indexColor(weather.thermalIndex.slope)} strokeWidth="2.5"
                          strokeDasharray={`${weather.thermalIndex.slope * 10}, 100`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[15px] font-black"
                        style={{ color: indexColor(weather.thermalIndex.slope) }}>
                        {weather.thermalIndex.slope.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* Cloud Base — Public-Reduced 2026-05-08: nur eingeloggt sichtbar */}
                  {session?.user && (
                    <div className="rounded-[12px] p-3 text-center flex flex-col justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}
                      data-tip={t('weather.tipCloudBase')}>
                      <div className="text-[9px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1">{t('weather.cloudBase')}</div>
                      <div className="text-[22px] font-black text-[var(--text)]">
                        {units === 'metric' ? weather.thermalIndex.cloudBase : Math.round(toFt(weather.thermalIndex.cloudBase))}
                        <span className="text-[11px] font-bold text-[var(--muted)] ms-0.5">{units === 'metric' ? 'm' : 'ft'}</span>
                      </div>
                    </div>
                  )}

                  {/* Thermal Strength */}
                  <div className="rounded-[12px] p-3 text-center flex flex-col justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}
                    data-tip={t('weather.tipThermalStrength')}>
                    <div className="text-[9px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1">{t('weather.thermalStrength')}</div>
                    <div className="text-[22px] font-black text-[var(--text)]">
                      {weather.thermalIndex.thermalStrength.toFixed(1)}
                      <span className="text-[11px] font-bold text-[var(--muted)] ms-0.5">m/s</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ══════ Current Conditions Card ══════ */}
            <div className="rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[12px] font-black tracking-[.06em] uppercase text-[var(--text)]">
                  {t('weather.currentConditions')}
                </h3>
                <span className="text-[12px] font-black tracking-[.06em] text-[var(--muted)]" data-tip={`UTC: ${new Date(weather.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}`}>
                  {new Date(weather.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: weather.timezone || 'UTC' })}
                  <span className="text-[9px] ms-1 opacity-60">
                    ({new Date(weather.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC)
                  </span>
                </span>
              </div>

              <div className="flex items-center justify-between mb-4 px-1">
                <span className="text-[32px] leading-none">{weatherEmoji(weather.current.icon)}</span>
                <div className="text-[28px] font-black text-[var(--text)] leading-none text-center">
                  {units === 'metric' ? weather.current.temperature.toFixed(1) : toF(weather.current.temperature).toFixed(1)}
                  <span className="text-[14px] text-[var(--muted)]">&deg;{units === 'metric' ? 'C' : 'F'}</span>
                </div>
                <button
                  onClick={() => setUnits(u => u === 'metric' ? 'imperial' : 'metric')}
                  className="wertung-tab-pill inline-flex items-center justify-center w-9 h-9 rounded-full text-[11px] font-black uppercase tracking-wider cursor-pointer"
                  data-state="inactive"
                  data-tip={units === 'metric' ? 'Zu °F / mph wechseln' : 'Zu °C / km/h wechseln'}
                >
                  {units === 'metric' ? '°F' : '°C'}
                </button>
              </div>

              <div className="space-y-2">
                {/* Wind */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.wind')}</span>
                  <span className="text-[13px] font-black text-[var(--text)] flex items-center gap-1.5">
                    {units === 'metric' ? `${weather.current.windSpeed.toFixed(0)} km/h` : `${toMph(weather.current.windSpeed).toFixed(0)} mph`}
                    <span className="text-[11px] font-black text-[var(--accent)]" data-tip={`${weather.current.windDirection}°`}>
                      {degToCompass(weather.current.windDirection)}
                    </span>
                    {weather.current.windGust > 0 && (
                      <span className="text-[11px] text-[var(--muted)]">
                        ({t('weather.gust')} {units === 'metric' ? weather.current.windGust.toFixed(0) : toMph(weather.current.windGust).toFixed(0)})
                      </span>
                    )}
                  </span>
                </div>

                {/* Cloud Cover */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.cloudCover')}</span>
                  <span className="text-[13px] font-black text-[var(--text)]">{weather.current.cloudCover}%</span>
                </div>

                {/* Precipitation */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.precipitation')}</span>
                  <span className="text-[13px] font-black text-[var(--text)]">
                    {units === 'metric' ? `${weather.current.precipitation.toFixed(1)} mm` : `${(weather.current.precipitation / 25.4).toFixed(2)} in`}
                  </span>
                </div>

                {/* Pressure */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.pressure')}</span>
                  <span className="text-[13px] font-black text-[var(--text)]">
                    {units === 'metric' ? `${weather.current.pressure.toFixed(0)} hPa` : `${toInHg(weather.current.pressure).toFixed(2)} inHg`}
                  </span>
                </div>

                {/* Dew Point */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.dewPoint')}</span>
                  <span className="text-[13px] font-black text-[var(--text)]">
                    {units === 'metric' ? weather.current.dewPoint.toFixed(1) : toF(weather.current.dewPoint).toFixed(1)}&deg;{units === 'metric' ? 'C' : 'F'}
                  </span>
                </div>

                {/* Sunshine */}
                <div className="flex items-center justify-between" data-tip="Sonnenscheindauer in der letzten Stunde (in Minuten)">
                  <span className="text-[12px] font-bold text-[var(--muted)]">{t('weather.sunshine')}</span>
                  <span className="text-[13px] font-black text-[var(--text)]">{weather.current.sunshine} min/h</span>
                </div>
              </div>

              {/* Quelle */}
              <p className="text-[9px] text-[var(--muted)] text-center mt-3 pt-2 border-t border-[var(--border)]">
                {weather.source}{weather.station ? ` · ${weather.station}` : ''}
              </p>
            </div>

            {/* ══════ VFR Info Bar ══════ */}
            {weather.sun && (
              <div className="lg:col-span-3 rounded-[20px] border border-[var(--accent)]/15 px-5 py-3" style={{ background: 'rgba(242,201,78,0.03)' }}>
                {/* R100.57 (2026-05-30): UTC + Local-Time-Hinweiszeile.
                    Zeiten werden in UTC angezeigt. Bei erkannter Ortsangabe oder
                    Standortfreigabe wird zusätzlich die jeweilige Local Time der
                    zugehörigen Zeitzone dargestellt. */}
                {weather.timezone && weather.timezone !== 'UTC' && (
                  <div className="text-[11px] text-[var(--muted)] mb-1.5 leading-snug">
                    Zeiten in <strong className="text-[var(--text)]">UTC</strong>. Lokale Zeitzone deines Standorts:{' '}
                    <strong className="text-[var(--text)]">{weather.timezone}</strong> — aktuell{' '}
                    <strong className="text-[var(--accent)]">
                      {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: weather.timezone })}
                    </strong>{' '}Local.
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                  {/* Sunrise / Sunset */}
                  <div className="flex items-center gap-4">
                    <span
                      className="flex items-center gap-1.5 text-[13px] font-black text-[var(--text)]"
                      data-tip={weather.timezone && weather.timezone !== 'UTC'
                        ? `Sonnenaufgang in UTC — Local: ${weather.sun.sunrise} UTC entspricht ca. ${formatUtcAsLocal(weather.sun.sunrise, weather.timezone)} ${weather.timezone}`
                        : 'Sonnenaufgang — astronomisch berechnet für diesen Standort'}
                    >
                      <span className="text-[16px]">&#9728;&#65039;</span>
                      {weather.sun.sunrise} UTC
                    </span>
                    <span
                      className="flex items-center gap-1.5 text-[13px] font-black text-[var(--text)]"
                      data-tip={weather.timezone && weather.timezone !== 'UTC'
                        ? `Sonnenuntergang in UTC — Local: ${weather.sun.sunset} UTC entspricht ca. ${formatUtcAsLocal(weather.sun.sunset, weather.timezone)} ${weather.timezone}`
                        : 'Sonnenuntergang'}
                    >
                      <span className="text-[16px]">&#127769;</span>
                      {weather.sun.sunset} UTC
                    </span>
                  </div>

                  {/* VFR Window */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-bold tracking-[.06em] uppercase text-[var(--muted)]">VFR:</span>
                    <span
                      className="text-[13px] font-black text-[#f2c94e]"
                      data-tip="Beginn bürgerliche Dämmerung — frühestmöglicher VFR-Modellflug"
                    >
                      {weather.sun.vfrStart}
                    </span>
                    <span className="text-[11px] text-[var(--muted)]">&ndash;</span>
                    <span
                      className="text-[13px] font-black text-[#f2c94e]"
                      data-tip="Ende bürgerliche Dämmerung — spätestmöglicher VFR-Modellflug"
                    >
                      {weather.sun.vfrEnd}
                    </span>
                    <span className="text-[11px] text-[var(--muted)]">UTC</span>
                  </div>

                  {/* Daylight Hours */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-bold tracking-[.06em] uppercase text-[var(--muted)]">{t('weather.daylight')}</span>
                    <span className="text-[13px] font-black text-[var(--text)]" data-tip="Stunden zwischen Sonnenaufgang und Sonnenuntergang">
                      {weather.sun.daylightHours} h
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ══════ Modellflug-Index Erklärung ══════ */}
            <details className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden">
              <summary className="px-6 py-4 cursor-pointer text-[12px] font-black tracking-[.06em] uppercase text-[var(--muted)] hover:text-[var(--accent)] transition-colors flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                {t('weather.explainSummary')}
              </summary>
              <div className="px-6 pb-5 space-y-5">
                {/* Gesamtindex */}
                <div>
                  <h4 className="text-[12px] font-black text-[var(--accent)] uppercase tracking-wider mb-2">{t('weather.totalIndexTitle')}</h4>
                  <div className="rounded-xl bg-[var(--bg-secondary)] p-3 text-[11px] text-[var(--muted)] space-y-1">
                    <p><strong className="text-[var(--text)]">{t('weather.formulaLabel')}:</strong> max(Thermik, Hang) + min(Thermik, Hang) × 0.25</p>
                    <p>{t('weather.totalIndexDescription')}</p>
                    <p className="text-[10px] opacity-70">{t('weather.exampleLabel')}: Thermik 5.0 + Hang 7.0 → max(7.0) + 5.0 × 0.25 = <strong className="text-[var(--accent)]">8.25</strong></p>
                  </div>
                </div>

                {/* Bewertungsskala */}
                <div>
                  <h4 className="text-[12px] font-black text-[var(--text)] uppercase tracking-wider mb-2">{t('weather.ratingScaleTitle')}</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {[
                      { range: '0–2', label: t('weather.ratingUnfavorable'), color: '#ef4444', desc: t('weather.ratingUnfavorableDesc') },
                      { range: '3–4', label: t('weather.ratingRestricted'), color: '#f97316', desc: t('weather.ratingRestrictedDesc') },
                      { range: '5–6', label: t('weather.ratingDecent'), color: '#eab308', desc: t('weather.ratingDecentDesc') },
                      { range: '7–8', label: t('weather.ratingGood'), color: '#84cc16', desc: t('weather.ratingGoodDesc') },
                      { range: '9–10', label: t('weather.ratingExcellent'), color: '#22c55e', desc: t('weather.ratingExcellentDesc') },
                    ].map(level => (
                      <div key={level.range} className="p-2.5 rounded-xl bg-[var(--bg-secondary)] text-center">
                        <div className="text-[16px] font-black mb-0.5" style={{ color: level.color }}>{level.range}</div>
                        <div className="text-[10px] font-black text-[var(--text)] mb-0.5">{level.label}</div>
                        <div className="text-[9px] text-[var(--muted)]">{level.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Thermik-Index */}
                <div>
                  <h4 className="text-[12px] font-black text-[var(--text)] uppercase tracking-wider mb-2">☀ {t('weather.thermalIndexExplainTitle')}</h4>
                  <div className="text-[11px] text-[var(--muted)] space-y-1">
                    <p><strong className="text-[var(--text)]">{t('weather.baseLabel')}:</strong> {t('weather.thermalIndexBase')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.windDampLabel')}:</strong> {t('weather.windDampValue')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.gustPenaltyLabel')}:</strong> {t('weather.gustPenaltyValue')}</p>
                    <p className="text-[10px] opacity-70">{t('weather.thermalIndexExample')}</p>
                  </div>
                </div>

                {/* Hang-Index */}
                <div>
                  <h4 className="text-[12px] font-black text-[var(--text)] uppercase tracking-wider mb-2">⛰ {t('weather.slopeIndexExplainTitle')}</h4>
                  <div className="text-[11px] text-[var(--muted)] space-y-1">
                    <p><strong className="text-[var(--text)]">{t('weather.baseLabel')}:</strong> {t('weather.slopeIndexBase')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.windDirLabel')}:</strong> {t('weather.windDirValue')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.precipLabel')}:</strong> {t('weather.precipValue')}</p>
                  </div>
                </div>

                {/* Datenquellen */}
                <div className="pt-2 border-t border-[var(--border)]">
                  <h4 className="text-[12px] font-black text-[var(--text)] uppercase tracking-wider mb-2">{t('weather.dataSourcesTitle')}</h4>
                  <div className="text-[11px] text-[var(--muted)] space-y-1">
                    <p><strong className="text-[var(--text)]">{t('weather.sourceGermany')}:</strong> {t('weather.sourceGermanyValue')}</p>
                    <p><strong className="text-[var(--text)]">USA:</strong> {t('weather.sourceUsaValue')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.sourceJapan')}:</strong> {t('weather.sourceJapanValue')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.sourceWorldwide')}:</strong> {t('weather.sourceWorldwideValue')}</p>
                    <p><strong className="text-[var(--text)]">{t('weather.licenseLabel')}:</strong> {t('weather.licenseValue')}</p>
                  </div>
                </div>
              </div>
            </details>

            {/* ══════ Allgemeine Hinweise ══════ */}
            <div className="lg:col-span-3 text-[10px] text-[var(--muted)] flex flex-wrap gap-x-4 gap-y-1 px-2">
              <span data-tip={t('weather.tipsCloudBaseTip')}><strong>{t('weather.tipsCloudBaseLabel')}:</strong> 125 × (T − Taupunkt) m AGL</span>
              <span data-tip={t('weather.tipsVfrTip')}><strong>VFR:</strong> {t('weather.tipsVfrValue')}</span>
              <span data-tip={t('weather.tipsSourceTip')}><strong>{t('weather.tipsSourceLabel')}:</strong> DWD (DACH) · NOAA (USA) · JMA (Japan) · MET Norway / Yr.no (72h)</span>
            </div>

            {/* ══════ AB HIER: NUR FÜR EINGELOGGTE USER (Start+) ══════ */}
            {!isLoggedIn && (
              <div className="lg:col-span-3 rounded-[20px] border border-[var(--accent)]/20 p-8 text-center" style={{ background: 'rgba(242,201,78,0.03)' }}>
                <div className="text-[24px] mb-3">&#9728;&#65039;</div>
                <p className="text-[16px] font-black text-[var(--accent)] mb-2">{t('weather.unlockMoreDetails')}</p>
                <p className="text-[12px] text-[var(--muted)] mb-3 max-w-[550px] mx-auto leading-relaxed">
                  {t('weather.unlockBody')}
                </p>
                <p className="text-[11px] text-[var(--muted)] mb-5 max-w-[500px] mx-auto opacity-70">
                  {t('weather.unlockPlatformActive')}
                </p>
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border-2 border-[var(--accent)] bg-[var(--accent)]/10 text-[12px] font-black text-[var(--accent)] hover:bg-[var(--accent)]/20 transition-all"
                >
                  {t('weather.loginButton')}
                </Link>
              </div>
            )}

            {isLoggedIn && <>

            {/* ══════ DWD GAFOR-Gebiete (Klickbare Karte) — VOR der Tabelle ══════ */}
            <details className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden">
              <summary className="px-6 py-4 cursor-pointer text-[12px] font-black tracking-[.06em] uppercase text-[var(--muted)] hover:text-[var(--accent)] transition-colors flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {t('weather.gaforSectionTitle')}
              </summary>
              <div className="px-6 pb-6">
                <p className="text-[11px] text-[var(--muted)] mb-4">
                  {t('weather.gaforSectionBody')}
                </p>
                <GaforMap
                  activeRegion={soaringForecast?.regionId}
                  onSelectRegion={async (regionId) => {
                    const region = getRegionById(regionId)
                    if (region) {
                      setLocation({ lat: region.center.lat, lon: region.center.lon })
                      setLocationName(`GAFOR ${region.id} — ${region.name} (${region.referenceStation})`)
                    }
                  }}
                  forecasts={allGaforData.size > 0 ? allGaforData : soaringForecast?.regionId ? new Map([[soaringForecast.regionId, {
                    climbRate: soaringForecast.summary?.maxClimbRate,
                    pfd: soaringForecast.summary?.pfdDaily,
                    windSpeed: soaringForecast.hourly?.find((h: any) => h.windSpeed)?.windSpeed,
                    windGust: soaringForecast.hourly?.find((h: any) => h.windGust)?.windGust,
                    windDirection: soaringForecast.hourly?.find((h: any) => h.windDirection)?.windDirection,
                    thermalIndex: weather?.thermalIndex?.flatland,
                    slopeIndex: weather?.thermalIndex?.slope,
                  }]]) : undefined}
                />
              </div>
            </details>

            {/* ══════ VLOS-Tagesvorhersage — "Was schaffst du heute?" ══════ */}
            {potential && potential.hourly?.length > 0 && (() => {
              const ratingColor = (r: string) => {
                if (r === 'top') return '#22c55e'
                if (r === 'good') return '#84cc16'
                if (r === 'mid') return '#eab308'
                if (r === 'weak') return '#f97316'
                return 'var(--muted)'
              }
              const ratingLabel = (r: string) => {
                if (r === 'top') return 'Top'
                if (r === 'good') return 'Gut'
                if (r === 'mid') return 'Mittel'
                if (r === 'weak') return 'Schwach'
                return '–'
              }
              return (
                <div className="lg:col-span-3 rounded-[20px] border border-[var(--accent)]/40 bg-[var(--bg-card)] p-5 sm:p-6"
                     style={{ background: 'linear-gradient(135deg, rgba(242,201,78,0.04), rgba(242,201,78,0.01))' }}>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div>
                      <h3 className="text-[15px] font-black tracking-[.06em] uppercase text-[var(--accent)]">
                        Was schaffst du heute?
                      </h3>
                      <p className="text-[12px] text-[var(--muted)] mt-0.5">
                        VLOS-Tagesvorhersage · {potential.regionName} ({potential.forecastDate})
                        {potential.modelName && <> · {potential.modelName}</>}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-[.10em] text-[var(--muted)]">
                        Erwartbar in Sichtweite
                      </div>
                      <div className="text-[28px] font-black text-[var(--accent)] leading-tight">
                        {potential.expectedDistanceKmMin}–{potential.expectedDistanceKmMax} km
                      </div>
                    </div>
                  </div>

                  {/* DSQ-Banner — FGL Hard-Limit 750m AGL + Eigenverantwortung Pilot
                      2026-05-15: DWD-Arbeitshöhe ist segelflug-physikalisch (Großsegler).
                      Für FGL-Wertung gilt 750 m AGL als harte Obergrenze (Wettbewerbsordnung § 4).
                      Lokale/gesetzliche Höhenbeschränkungen können niedriger sein → Pilot ist verantwortlich. */}
                  <div
                    className="mb-4 rounded-xl px-4 py-3 flex items-start gap-3"
                    style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.40)' }}
                  >
                    <span className="text-[18px] mt-0.5" style={{ color: '#ef4444' }}>⚠</span>
                    <div>
                      <div className="text-[12px] font-black uppercase tracking-[.08em] mb-0.5" style={{ color: '#ef4444' }}>
                        FGL-Obergrenze 750 m AGL — Eigenverantwortung
                      </div>
                      <div className="text-[11px] text-[var(--muted)] leading-snug">
                        DWD-Arbeitshöhe und Wolkenbasis sind segelflug-physikalische Werte für bemannten Segelflug.
                        Für die FGL-Wertung gilt international <strong className="text-[var(--text)]">750 m AGL</strong> als harte Obergrenze
                        (siehe <a href="/wettbewerbsordnung" className="underline">Wettbewerbsordnung</a>).
                        Zusätzlich können <strong className="text-[var(--text)]">lokale oder gesetzliche Höhenbeschränkungen</strong>
                        gelten (Lufträume, Modellfluggebiet-Auflagen, Naturschutz) — die Einhaltung liegt in der
                        <strong className="text-[var(--text)]"> Eigenverantwortung des Piloten</strong>.
                      </div>
                    </div>
                  </div>

                  {/* Summary-Zeile */}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mb-4 text-[12px]">
                    {potential.recommendedStart && (
                      <span className="text-[var(--text)]">
                        <span className="text-[var(--muted)] text-[10px]">Start ab </span>
                        <strong>{potential.recommendedStart}</strong>
                      </span>
                    )}
                    {potential.recommendedEnd && (
                      <span className="text-[var(--text)]">
                        <span className="text-[var(--muted)] text-[10px]">Ende bis </span>
                        <strong>{potential.recommendedEnd}</strong>
                      </span>
                    )}
                    {potential.bestHours?.length > 0 && (
                      <span className="text-[var(--text)]">
                        <span className="text-[var(--muted)] text-[10px]">Beste Stunden </span>
                        <strong>{potential.bestHours.join(' · ')}</strong>
                      </span>
                    )}
                    <span className="text-[var(--text)]">
                      <span className="text-[var(--muted)] text-[10px]">VLOS-Radius </span>
                      <strong>{potential.vlosRadiusM} m</strong>
                    </span>
                  </div>

                  {/* Stündliche Rating-Leiste */}
                  <div className="overflow-x-auto scrollbar-none -mx-1 rounded-xl border border-[var(--border)] mb-4">
                    <table className="w-full border-collapse" style={{ minWidth: `${90 + potential.hourly.length * 50}px` }}>
                      <thead>
                        <tr className="border-b border-[var(--border)]">
                          <th className="sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap min-w-[90px] text-start">Stunde</th>
                          {potential.hourly.map((h: any, i: number) => (
                            <th key={i} className="text-[10px] font-bold text-[var(--muted)] text-center px-1 py-1 whitespace-nowrap min-w-[50px]">
                              {h.hour?.replace(':00', '')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-[var(--border)]/50">
                          <td className="sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap text-start">VLOS km/h</td>
                          {potential.hourly.map((h: any, i: number) => (
                            <td key={i}
                                className="text-center text-[11px] font-bold px-1 py-1"
                                style={{ color: h.vlosEffectiveKm ? ratingColor(h.rating) : 'var(--muted)' }}>
                              {h.vlosEffectiveKm ?? '—'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-[var(--border)]/50">
                          <td className="sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap text-start">Steigen m/s</td>
                          {potential.hourly.map((h: any, i: number) => (
                            <td key={i}
                                className="text-center text-[11px] font-bold px-1 py-1"
                                style={{ color: h.forecastClimbMs != null && h.forecastClimbMs > 0 ? ratingColor(h.rating) : 'var(--muted)' }}>
                              {h.forecastClimbMs != null && h.forecastClimbMs > 0 ? h.forecastClimbMs.toFixed(1) : '—'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-[var(--border)]/50">
                          <td className="sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap text-start"
                              data-tip="Wolkenbasis (LCL) aus Bodentemperatur und Taupunkt geschätzt. Wenn höher als 750 m AGL, bleibt 750 m trotzdem die harte Obergrenze.">
                            Wolkenbasis AGL
                          </td>
                          {potential.hourly.map((h: any, i: number) => {
                            const cb = h.cloudBaseAglM
                            const dsqOver = cb != null && cb > 750
                            return (
                              <td key={i}
                                  className="text-center text-[11px] font-bold px-1 py-1"
                                  style={{ color: cb == null || cb === 0 ? 'var(--muted)' : dsqOver ? '#ef4444' : '#22c55e' }}>
                                {cb != null && cb > 0 ? `${cb} m` : '—'}
                              </td>
                            )
                          })}
                        </tr>
                        <tr>
                          <td className="sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap text-start">Rating</td>
                          {potential.hourly.map((h: any, i: number) => (
                            <td key={i}
                                className="text-center text-[10px] font-bold px-1 py-1"
                                style={{ color: ratingColor(h.rating) }}>
                              {ratingLabel(h.rating)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Notes + similar day */}
                  <div className="space-y-1.5">
                    {potential.notes?.map((note: string, i: number) => (
                      <p key={i} className="text-[12px] text-[var(--muted)] flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-0.5">·</span> {note}
                      </p>
                    ))}
                    {potential.similarDay && (
                      <p className="text-[12px] text-[var(--muted)] flex items-start gap-2 pt-2 border-t border-[var(--border)] mt-2">
                        <span className="text-[var(--accent)] mt-0.5">↺</span>
                        <span>
                          Dein ähnlichster Tag (<a href={`/app/flights/${potential.similarDay.flightId}`} className="text-[var(--accent)] hover:underline">{potential.similarDay.flightDate}</a>): {potential.similarDay.distanceKm} km
                          {potential.similarDay.maxAltitudeM && <>, {potential.similarDay.maxAltitudeM}m max</>}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* ══════ DWD Segelflugwetter (echte Daten) — Redesign ══════ */}
            {soaringForecast?.hourly?.length > 0 && (() => {
              // Alle DWD-Stunden 06-23 wie im Original (gleiche Spalten wie DWD)
              const dwdHours = soaringForecast.hourly.filter((h: any) => {
                const hr = parseInt(h.time?.split(':')[0] || '0', 10)
                return hr >= 6 && hr <= 23
              })
              const hourLabels = dwdHours.map((h: any) => h.time?.replace(':00', '') || '?')

              // Area data arrays (same index as dwdHours via hour matching)
              const getAreaVal = (area: any, field: string, hourIdx: number): number | null => {
                if (!area?.[field]) return null
                // area arrays are indexed by the full hourly array, map via hour index
                const fullHours = soaringForecast.hourly as any[]
                const targetTime = dwdHours[hourIdx]?.time
                const fullIdx = fullHours.findIndex((fh: any) => fh.time === targetTime)
                if (fullIdx < 0 || fullIdx >= area[field].length) return null
                return area[field][fullIdx]
              }

              // Summary helpers
              const avgStart = soaringForecast.areaAverage?.thermalStart || soaringForecast.summary?.thermalStart
              const avgEnd = soaringForecast.areaAverage?.thermalEnd || soaringForecast.summary?.thermalEnd
              const maxStart = soaringForecast.areaMaximum?.thermalStart
              const maxEnd = soaringForecast.areaMaximum?.thermalEnd

              // Tagesleistung berechnen: Summe aller Strecke/h über die Thermik-Stunden
              const calcDailyPerformance = (areaData: any, hourlyFallback: any[]) => {
                if (!areaData?.pfdPerHour && !hourlyFallback) return null
                const vals = areaData?.pfdPerHour || hourlyFallback.map((h: any) => h.pfdPerHour)
                if (!Array.isArray(vals)) return null
                const sum = vals.reduce((s: number, v: number | null) => s + Math.round((v || 0) / 2), 0)
                return sum > 0 ? sum : null
              }
              const avgDailyKm = calcDailyPerformance(soaringForecast.areaAverage, soaringForecast.hourly)
              const maxDailyKm = calcDailyPerformance(soaringForecast.areaMaximum, [])

              // Climb rate color
              const climbColor = (v: number) => v >= 1.5 ? '#22c55e' : v >= 0.8 ? '#eab308' : v > 0 ? '#f97316' : 'var(--muted)'

              // Cell style for the hourly table
              const cellCls = 'min-w-[44px] text-center text-[11px] font-bold px-1 py-1 whitespace-nowrap'
              const labelCls = 'sticky start-0 z-10 bg-[var(--bg-card)] text-[10px] font-black text-[var(--muted)] uppercase tracking-[.04em] px-2 py-1 whitespace-nowrap min-w-[90px] text-start'

              return (
                <div className="lg:col-span-3 rounded-[20px] border border-[var(--accent)]/30 bg-[var(--bg-card)] p-5 sm:p-6">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-[14px] font-black tracking-[.06em] uppercase text-[var(--accent)]">
                      DWD Segelflugvorhersage
                    </h3>
                    <p className="text-[11px] text-[var(--muted)] mt-0.5">
                      GAFOR {soaringForecast.regionId} — {soaringForecast.regionName} ({soaringForecast.referenceStation}) · {soaringForecast.model}
                    </p>
                  </div>

                  {/* DSQ-Banner — FGL Hard-Limit 750m AGL + Eigenverantwortung Pilot (2026-05-15) */}
                  <div
                    className="mb-4 rounded-xl px-4 py-3 flex items-start gap-3"
                    style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.40)' }}
                  >
                    <span className="text-[18px] mt-0.5" style={{ color: '#ef4444' }}>⚠</span>
                    <div>
                      <div className="text-[12px] font-black uppercase tracking-[.08em] mb-0.5" style={{ color: '#ef4444' }}>
                        FGL-Obergrenze 750 m AGL — Eigenverantwortung
                      </div>
                      <div className="text-[11px] text-[var(--muted)] leading-snug">
                        Die DWD-Arbeitshöhe ist segelflug-physikalisch (für bemannten Segelflug).
                        Für die FGL-Wertung gilt international <strong className="text-[var(--text)]">750 m AGL</strong> als harte Obergrenze
                        (siehe <a href="/wettbewerbsordnung" className="underline">Wettbewerbsordnung</a>).
                        Zusätzlich können <strong className="text-[var(--text)]">lokale oder gesetzliche Höhenbeschränkungen</strong>
                        gelten — die Einhaltung liegt in der <strong className="text-[var(--text)]">Eigenverantwortung des Piloten</strong>.
                      </div>
                    </div>
                  </div>

                  {/* Sonnenaufgang / Sonnenuntergang + Dämmerung */}
                  {weather?.sun && (
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-5 px-1 text-[12px] font-bold text-[var(--text)]">
                      <span data-tip="Sonnenaufgang UTC">&#9728;&#65039; Sonnenaufgang: {weather.sun.sunrise} UTC</span>
                      <span data-tip="Sonnenuntergang UTC">&#127769; Sonnenuntergang: {weather.sun.sunset} UTC</span>
                      {weather.sun.civilDawn && weather.sun.civilDusk && (
                        <span className="text-[11px] text-[var(--muted)]" data-tip="Bürgerliche Dämmerung — VFR-Modellflug möglich">
                          Bürgerl. Dämmerung: {weather.sun.civilDawn} — {weather.sun.civilDusk} UTC
                        </span>
                      )}
                    </div>
                  )}

                  {/* Summary Cards: Gebietsmittel + Maximum — compact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    {/* Gebietsmittel Summary */}
                    <div className="rounded-xl border border-[var(--border)] p-3">
                      <h4 className="text-[10px] font-black tracking-[.08em] uppercase text-[var(--muted)] mb-2">
                        Gebietsmittel
                      </h4>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                        {avgStart && <span className="text-[var(--text)]"><span className="text-[var(--muted)] text-[10px]">Beginn </span><strong>{avgStart}</strong></span>}
                        {avgEnd && <span className="text-[var(--text)]"><span className="text-[var(--muted)] text-[10px]">Ende </span><strong>{avgEnd}</strong></span>}
                        {soaringForecast.summary?.avgClimbRate != null && <span className="text-[var(--text)]"><span className="text-[var(--muted)] text-[10px]">Max. Steigen </span><strong>{soaringForecast.summary.avgClimbRate} m/s</strong></span>}
                        {avgDailyKm != null && <span className="text-[var(--text)]" data-tip="⌀ Tagesleistung: Summe aller Strecke/h über die Thermik-Stunden (Gebietsmittel). Mögliche Tagesbestleistung in der Standardklasse der FREE GLIDE LEAGUE bei durchschnittlichen Bedingungen im Gebiet."><span className="text-[var(--muted)] text-[10px]">Tagesleistung </span><strong>{avgDailyKm} km</strong></span>}
                      </div>
                    </div>
                    {/* Maximum Summary */}
                    <div className="rounded-xl border border-[var(--accent)]/30 p-3" style={{ background: 'rgba(242,201,78,0.03)' }}>
                      <h4 className="text-[10px] font-black tracking-[.08em] uppercase text-[var(--accent)] mb-2">
                        Maximum
                      </h4>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                        {maxStart && <span className="text-[var(--text)]"><span className="text-[var(--muted)] text-[10px]">Beginn </span><strong>{maxStart}</strong></span>}
                        {maxEnd && <span className="text-[var(--text)]"><span className="text-[var(--muted)] text-[10px]">Ende </span><strong>{maxEnd}</strong></span>}
                        {soaringForecast.summary?.maxClimbRate != null && <span className="text-[var(--accent)]"><span className="text-[var(--muted)] text-[10px]">Max. Steigen </span><strong>{soaringForecast.summary.maxClimbRate} m/s</strong></span>}
                        {maxDailyKm != null && <span className="text-[var(--accent)]" data-tip="↑ Tagesleistung (Maximum): Summe aller Strecke/h an der besten Stelle im Gebiet. Bestmögliche Tagesleistung in der Standardklasse der FREE GLIDE LEAGUE unter optimalen Bedingungen."><span className="text-[var(--muted)] text-[10px]">Tagesleistung </span><strong>{maxDailyKm} km</strong></span>}
                      </div>
                    </div>
                  </div>

                  {/* ═══ THERMIK (EBENE) ═══ */}
                  <div className="mb-5">
                    <h4 className="text-[11px] font-black tracking-[.10em] uppercase text-[var(--text)] mb-2 flex items-center gap-1.5">
                      <span className="text-[14px]">&#9728;&#65039;</span> Thermik (Ebene)
                    </h4>
                    <div className="overflow-x-auto scrollbar-none -mx-1 rounded-xl border border-[var(--border)]">
                      <table className="w-full border-collapse" style={{ minWidth: `${90 + dwdHours.length * 44}px` }}>
                        <thead>
                          <tr className="border-b border-[var(--border)]">
                            <th className={labelCls}>Stunde</th>
                            {hourLabels.map((h: string, i: number) => (
                              <th key={i} className={`${cellCls} text-[10px] font-bold text-[var(--muted)]`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Steigen Gebietsmittel */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="⌀ Steigen (Gebietsmittel): Durchschnittliche Aufwindgeschwindigkeit im gesamten GAFOR-Gebiet in m/s. Typisch für RC-Modellsegelflug: 0.3–1.5 m/s nutzbar.">Steigen &#8960;</td>
                            {dwdHours.map((h: any, i: number) => {
                              const v = getAreaVal(soaringForecast.areaAverage, 'avgClimbRate', i) ?? h.avgClimbRate
                              return <td key={i} className={cellCls} style={{ color: v != null && v > 0 ? climbColor(v) : 'var(--muted)' }}>{v != null && v > 0 ? v.toFixed(1) : '\u2014'}</td>
                            })}
                          </tr>
                          {/* Steigen Maximum */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="↑ Steigen (Maximum): Bestes Steigen an der stärksten Stelle im GAFOR-Gebiet in m/s. Mögliche Tagesbestleistung in der Standardklasse der FREE GLIDE LEAGUE.">Steigen &#8593;</td>
                            {dwdHours.map((_: any, i: number) => {
                              const v = getAreaVal(soaringForecast.areaMaximum, 'avgClimbRate', i)
                              return <td key={i} className={cellCls} style={{ color: v != null && v > 0 ? climbColor(v) : 'var(--muted)' }}>{v != null && v > 0 ? v.toFixed(1) : '\u2014'}</td>
                            })}
                          </tr>
                          {/* Modellflug-Strecke Gebietsmittel (PFD/2) */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="⌀ Strecke/h (Gebietsmittel): Erreichbare Strecke pro Stunde im Durchschnitt des GAFOR-Gebiets. Berechnet aus DWD-PFD halbiert für den Modellflug-Maßstab (geringere Gleitzahl als Segelflugzeuge).">Strecke/h &#8960;</td>
                            {dwdHours.map((h: any, i: number) => {
                              const v = getAreaVal(soaringForecast.areaAverage, 'pfdPerHour', i) ?? h.pfdPerHour
                              const display = v != null && v > 0 ? Math.round(v / 2) : null
                              return <td key={i} className={`${cellCls} text-[var(--text)]`}>{display != null ? display : '\u2014'}</td>
                            })}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>km</td>
                          </tr>
                          {/* Modellflug-Strecke Maximum (PFD/2) */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="↑ Strecke/h (Maximum): Bestmögliche Strecke pro Stunde an der stärksten Stelle im Gebiet. Mögliche Tagesbestleistung in der Standardklasse der FREE GLIDE LEAGUE. Halbierter DWD-PFD für Modellflug.">Strecke/h &#8593;</td>
                            {dwdHours.map((_: any, i: number) => {
                              const v = getAreaVal(soaringForecast.areaMaximum, 'pfdPerHour', i)
                              const display = v != null && v > 0 ? Math.round(v / 2) : null
                              return <td key={i} className={`${cellCls} text-[var(--accent)]`}>{display != null ? display : '\u2014'}</td>
                            })}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>km</td>
                          </tr>
                          {/* Bewölkung tief */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Tiefe Bewölkung (Stratus/Stratocumulus) in Achteln. 0/8 = wolkenfrei, 8/8 = bedeckt. Tiefe Wolken blockieren Thermik!">Bew. tief</td>
                            {dwdHours.map((h: any, i: number) => {
                              const v = h.cloudLow
                              return <td key={i} className={`${cellCls} text-[var(--muted)]`}>{v != null ? v : '\u2014'}</td>
                            })}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>/8</td>
                          </tr>
                          {/* Bewölkung mittel */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Mittlere Bewölkung (Altostratus/Cirrostratus) in Achteln. Beeinflusst Thermik weniger als tiefe Wolken, kann aber Sonneneinstrahlung reduzieren.">Bew. mittel</td>
                            {dwdHours.map((h: any, i: number) => {
                              const v = h.cloudMid
                              return <td key={i} className={`${cellCls} text-[var(--muted)]`}>{v != null ? v : '\u2014'}</td>
                            })}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>/8</td>
                          </tr>
                          {/* Bewölkung hoch */}
                          <tr>
                            <td className={labelCls} data-tip="Hohe Bewölkung (Cirrus/Cirrocumulus) in Achteln. Wenig Einfluss auf Thermik, zeigt aber Wetteränderungen an.">Bew. hoch</td>
                            {dwdHours.map((h: any, i: number) => {
                              const v = h.cloudHigh
                              return <td key={i} className={`${cellCls} text-[var(--muted)]`}>{v != null ? v : '\u2014'}</td>
                            })}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>/8</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ═══ HANGFLUG ═══ */}
                  <div className="mb-5">
                    <h4 className="text-[11px] font-black tracking-[.10em] uppercase text-[var(--text)] mb-2 flex items-center gap-1.5">
                      <span className="text-[14px]">&#9968;</span> Hangflug
                    </h4>
                    <div className="overflow-x-auto scrollbar-none -mx-1 rounded-xl border border-[var(--border)]">
                      <table className="w-full border-collapse" style={{ minWidth: `${90 + dwdHours.length * 44}px` }}>
                        <thead>
                          <tr className="border-b border-[var(--border)]">
                            <th className={labelCls}>Stunde</th>
                            {hourLabels.map((h: string, i: number) => (
                              <th key={i} className={`${cellCls} text-[10px] font-bold text-[var(--muted)]`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Wind Mittel */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="⌀ Wind (Mittel): Durchschnittliche Windgeschwindigkeit in km/h. Für Hangflug ideal: 15–25 km/h. Für Thermikflug in der Ebene: unter 15 km/h optimal.">Wind &#8960; km/h</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={`${cellCls} text-[var(--text)]`}>{h.windSpeed != null ? Math.round(h.windSpeed) : '\u2014'}</td>
                            ))}
                          </tr>
                          {/* Böen */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Windböen: Maximale Windspitzen in km/h. Bei Modellflug max. 25–30 km/h empfohlen. Über 40 km/h gefährlich für leichte Modelle!">Böen km/h</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={cellCls} style={{ color: (h.windGust || 0) > 40 ? '#ef4444' : (h.windGust || 0) > 25 ? '#f97316' : 'var(--text)' }}>{h.windGust != null ? Math.round(h.windGust) : '\u2014'}</td>
                            ))}
                          </tr>
                          {/* Windrichtung */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Windrichtung in Grad (0°=Nord, 90°=Ost, 180°=Süd, 270°=West). Für Hangflug entscheidend: Wind muss frontal auf den Hang treffen!">Richtung °</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={`${cellCls} text-[var(--muted)]`}>
                                {h.windDirection != null ? (
                                  <span className="inline-flex items-center gap-0.5">
                                    <svg width="10" height="10" viewBox="0 0 14 14" style={{ transform: `rotate(${windArrowDeg(h.windDirection)}deg)` }}>
                                      <path d="M7 2L9 10H5L7 2Z" fill="var(--accent)" />
                                    </svg>
                                    {Math.round(h.windDirection)}
                                  </span>
                                ) : '\u2014'}
                              </td>
                            ))}
                          </tr>
                          {/* Temperatur */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Lufttemperatur in °C. Großer Unterschied zum Taupunkt (Spread) = starke Thermik. Spread > 10°C = sehr gute Bedingungen.">Temp °C</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={`${cellCls} text-[var(--text)]`}>{h.temperature != null ? Math.round(h.temperature) : '\u2014'}</td>
                            ))}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>&deg;C</td>
                          </tr>
                          {/* Taupunkt */}
                          <tr className="border-b border-[var(--border)]/50">
                            <td className={labelCls} data-tip="Taupunkt in °C. Je tiefer der Taupunkt unter der Temperatur, desto trockener die Luft und desto höher die Wolkenbasis. Spread = Temperatur − Taupunkt.">Taupunkt °C</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={`${cellCls} text-[var(--muted)]`}>{h.dewpoint != null ? Math.round(h.dewpoint) : '\u2014'}</td>
                            ))}
                            <td className={`${cellCls} text-[9px] text-[var(--muted)]`}>&deg;C</td>
                          </tr>
                          {/* QNH */}
                          <tr>
                            <td className={labelCls} data-tip="QNH: Luftdruck reduziert auf Meereshöhe in hPa. Standarddruck 1013 hPa. Steigender QNH = Hochdrucklage (stabiles Wetter, gute Thermik). Fallender QNH = Tiefdruckeinfluss.">QNH hPa</td>
                            {dwdHours.map((h: any, i: number) => (
                              <td key={i} className={`${cellCls} text-[var(--muted)]`}>{h.qnh != null ? Math.round(h.qnh) : '\u2014'}</td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Hinweis + Quelle */}
                  <div className="p-3 rounded-xl bg-[rgba(239,68,68,0.05)] border border-[rgba(239,68,68,0.15)] mb-3">
                    <p className="text-[10px] text-[var(--muted)] leading-relaxed">
                      <strong className="text-red-400">&#9888; Hinweis:</strong> Max. <strong className="text-[var(--text)]">750 m AGL</strong> (FREE GLIDE LEAGUE).
                      Luftraum + NOTAM <strong className="text-[var(--text)]">eigenverantwortlich prüfen</strong>.
                      Strecke/h = PFD/2 (Modellflug-Maßstab). Tagesleistung = Summe aller Strecke/h über die Thermik-Stunden.
                      <a href="/flying-fields" className="text-[var(--accent)] hover:underline ms-1">Fluggebiete-Karte</a>
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                    <p className="text-[9px] text-[var(--muted)]">
                      Quelle: Deutscher Wetterdienst (DWD) · {soaringForecast.model} · {soaringForecast.forecastDate}
                    </p>
                    {soaringForecast.sourceUrl && (
                      <a href={soaringForecast.sourceUrl} target="_blank" rel="noopener noreferrer"
                        className="text-[9px] text-[var(--accent)] hover:underline">
                        DWD Original &rarr;
                      </a>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* GAFOR-Karte wurde nach oben verschoben (vor DWD-Tabelle) */}

            {/* ══════ Best Flying Hours — NUR wenn KEINE DWD-Daten (außerhalb DE) ══════ */}
            {!soaringForecast?.hourly?.length && (
            <div className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6">
              <h3 className="text-[14px] font-black tracking-[.06em] uppercase text-[var(--text)] mb-4">
                {t('weather.bestHours')}
              </h3>

              {todayForecast.length > 0 ? (
                <div className="overflow-x-auto scrollbar-none -mx-2">
                  <div className="flex gap-1.5 min-w-min px-2">
                    {todayForecast.filter(h => isInVfrWindow(new Date(h.time).getHours(), weather.sun)).map((h, i) => {
                      const hour = new Date(h.time).getHours()
                      const isBest = weather.thermalIndex.bestHours.includes(
                        `${String(hour).padStart(2, '0')}:00`
                      )
                      const fl = (h as any).flatlandIndex ?? h.thermalIndex
                      const sl = (h as any).slopeIndex ?? 0
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center gap-1 min-w-[48px] rounded-xl py-1.5 border border-[#f2c94e]/20 bg-[#f2c94e]/5"
                          data-tip={`${String(hour).padStart(2, '0')}:00 UTC — Thermik: ${fl.toFixed(1)} · Hang: ${sl.toFixed(1)}`}
                        >
                          {/* Hang-Index (oben) */}
                          <span className="text-[9px] font-black" style={{ color: indexColor(sl) }} data-tip={`Hang-Index ${sl.toFixed(1)}/10`}>
                            ⛰ {sl.toFixed(1)}
                          </span>
                          {/* Stunde */}
                          <span className="text-[10px] font-bold text-[var(--muted)]">
                            {String(hour).padStart(2, '0')}
                          </span>
                          {/* Bar */}
                          <div className="relative w-7 h-[80px] rounded-full bg-[var(--border)] overflow-hidden">
                            <div
                              className="absolute bottom-0 left-0 right-0 rounded-full transition-all"
                              style={{
                                height: `${h.thermalIndex * 10}%`,
                                backgroundColor: indexColor(h.thermalIndex),
                                opacity: 0.85,
                              }}
                            />
                          </div>
                          {/* Thermik-Index (unten) */}
                          <span className="text-[9px] font-black" style={{ color: indexColor(fl) }} data-tip={`Thermik-Index ${fl.toFixed(1)}/10`}>
                            ☀ {fl.toFixed(1)}
                          </span>
                          {isBest && (
                            <span className="text-[8px] font-black tracking-wider text-[var(--accent)] uppercase" data-tip="Optimale Flugzeit — Thermik-Index ≥ 5">
                              TOP
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-[var(--muted)] font-medium">{t('weather.noData')}</p>
              )}
            </div>
            )}

            {/* ══════ 72h Forecast Chart ══════ */}
            <div className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-[14px] font-black tracking-[.06em] uppercase text-[var(--text)]">
                  {t('weather.forecast72h')}
                </h3>
                {/* Day Tabs */}
                <div className="flex gap-1">
                  {dayLabels.map((label, idx) => (
                    <button
                      key={idx}
                      onClick={() => setForecastDayOffset(idx)}
                      className="wertung-tab-pill px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider cursor-pointer"
                      data-state={forecastDayOffset === idx ? 'active' : 'inactive'}
                      data-tip={dayDates[idx]}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Day date subtitle */}
              <p className="text-[11px] text-[var(--muted)] font-bold mb-3">{dayDates[forecastDayOffset]}</p>

              {selectedDayForecast.length > 0 ? (
                <div className="overflow-x-auto scrollbar-none -mx-2">
                  <div className="flex gap-0.5 min-w-min px-2">
                    {selectedDayForecast.filter(h => isInVfrWindow(new Date(h.time).getHours(), weather.sun)).map((h, i) => {
                      const d = new Date(h.time)
                      const hour = d.getHours()
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center gap-1 min-w-[50px] py-2 rounded-xl border border-[#f2c94e]/15 bg-[#f2c94e]/5"
                        >
                          <span className="text-[9px] font-bold text-[var(--muted)] flex items-center gap-0.5">
                            {String(hour).padStart(2, '0')}:00
                          </span>
                          <span className="text-[16px]">{weatherEmoji(h.icon)}</span>
                          <span className="text-[12px] font-black text-[var(--text)]" data-tip={`Vorhergesagte Temperatur um ${String(hour).padStart(2, '0')}:00`}>
                            {h.temperature.toFixed(0)}&deg;
                          </span>
                          {/* Wind */}
                          <div className="flex items-center gap-0.5" data-tip={`Windrichtung ${h.windDirection}° bei ${h.windSpeed.toFixed(0)} km/h`}>
                            <svg width="10" height="10" viewBox="0 0 14 14" style={{ transform: `rotate(${windArrowDeg(h.windDirection)}deg)` }}>
                              <path d="M7 2L9 10H5L7 2Z" fill="var(--muted)" />
                            </svg>
                            <span className="text-[10px] font-bold text-[var(--muted)]">
                              {h.windSpeed.toFixed(0)}
                            </span>
                          </div>
                          {/* Hang-Index */}
                          <span className="text-[8px] font-black" style={{ color: indexColor((h as any).slopeIndex ?? 0) }} data-tip={`Hang-Index ${((h as any).slopeIndex ?? 0).toFixed(1)}/10`}>
                            ⛰ {((h as any).slopeIndex ?? 0).toFixed(1)}
                          </span>
                          {/* Thermik-Index */}
                          <span className="text-[8px] font-black" style={{ color: indexColor((h as any).flatlandIndex ?? h.thermalIndex) }} data-tip={`Thermik-Index ${((h as any).flatlandIndex ?? h.thermalIndex).toFixed(1)}/10`}>
                            ☀ {((h as any).flatlandIndex ?? h.thermalIndex).toFixed(1)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-[var(--muted)] font-medium">{t('weather.noData')}</p>
              )}
            </div>

            {/* ══════ Empfohlene Fluggebiete ══════ */}
            <div className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-[14px] font-black tracking-[.06em] uppercase text-[var(--accent)]">
                    Fluggebiete in der Nähe
                  </h3>
                  <p className="text-[11px] text-[var(--muted)] font-medium mt-1">
                    Basierend auf Entfernung und aktuellem Wetter
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Terrain-Filter */}
                  {[
                    { key: 'all' as const, label: 'Alle' },
                    { key: 'flatland' as const, label: 'Thermik' },
                    { key: 'slope' as const, label: 'Hang' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setRecsTerrainFilter(f.key)}
                      className="wertung-tab-pill px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider cursor-pointer"
                      data-state={recsTerrainFilter === f.key ? 'active' : 'inactive'}
                    >
                      {f.label}
                    </button>
                  ))}
                  <span className="text-[11px] font-bold text-[var(--muted)]">
                    {t('weather.recommendationsRadius')}:
                  </span>
                  <select
                    value={recsRadius}
                    onChange={(e) => {
                      const r = parseInt(e.target.value)
                      setRecsRadius(r)
                      if (location) fetchRecommendations(location.lat, location.lon, r)
                    }}
                    className="rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[12px] font-bold text-[var(--text)] px-2 py-1 cursor-pointer"
                  >
                    <option value={5}>5 km</option>
                    <option value={10}>10 km</option>
                    <option value={15}>15 km</option>
                    <option value={25}>25 km</option>
                    <option value={50}>50 km</option>
                    <option value={75}>75 km</option>
                    <option value={100}>100 km</option>
                  </select>
                  <span className="text-[11px] font-bold text-[var(--muted)] ms-2">
                    Anzeige:
                  </span>
                  <select
                    value={recsLimit}
                    onChange={(e) => setRecsLimit(parseInt(e.target.value))}
                    className="rounded-lg border border-[var(--border)] bg-[var(--bg)] text-[12px] font-bold text-[var(--text)] px-2 py-1 cursor-pointer"
                  >
                    <option value={0}>Alle</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>

              {recsLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-3 border-[var(--accent)]/30 border-t-[var(--accent)] rounded-full animate-spin" />
                  <span className="ms-3 text-[12px] font-bold text-[var(--muted)]">
                    {t('weather.recommendationsLoading')}
                  </span>
                </div>
              )}

              {!recsLoading && recommendations.length === 0 && (
                <div className="text-center py-8">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 opacity-50">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <p className="text-[13px] text-[var(--muted)] font-medium">
                    {t('weather.recommendationsNoFields')}
                  </p>
                </div>
              )}

              {!recsLoading && recommendations.length > 0 && (() => {
                const terrainFiltered = recsTerrainFilter === 'all'
                  ? recommendations
                  : recommendations.filter((r: any) => {
                      const isSlopeField = r.terrain === 'slope' || r.description?.toLowerCase().includes('hang') || r.description?.toLowerCase().includes('slope')
                      return recsTerrainFilter === 'slope' ? isSlopeField : !isSlopeField
                    })
                const displayed = terrainFiltered.slice(0, recsLimit === 0 ? undefined : recsLimit)
                const slopeCount = displayed.filter(r => r.terrain === 'slope').length
                const flatCount = displayed.filter(r => r.terrain === 'flatland').length
                return (
                  <>
                    <div className="flex items-center gap-3 mb-3 text-[11px] font-bold text-[var(--muted)]">
                      <span>{displayed.length} von {terrainFiltered.length} Feldern{recsTerrainFilter !== 'all' ? ` (${recsTerrainFilter === 'slope' ? 'Hang' : 'Thermik'})` : ''}</span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
                        {flatCount} Thermik
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#f97316' }} />
                        {slopeCount} Hang
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      {displayed.map((rec, i) => (
                    <div
                      key={rec.id}
                      className="weather-card rounded-[14px] border border-[var(--border)] p-4 hover:border-[var(--accent)]/40 transition-colors"
                      style={{ background: indexBg(rec.terrain === 'slope' ? rec.slopeIndex : rec.thermalIndex) }}
                    >
                      {/* Rank badge */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--accent)] text-[11px] font-black text-black">
                            {i + 1}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase"
                            style={{
                              backgroundColor: rec.terrain === 'slope' ? '#f9731620' : '#22c55e20',
                              color: rec.terrain === 'slope' ? '#f97316' : '#22c55e',
                            }}
                            data-tip={rec.terrain === 'slope' ? 'Hangfluggelände' : 'Thermikgelände (Ebene)'}>
                            {rec.terrain === 'slope'
                              ? t('weather.recommendationsSlope')
                              : t('weather.recommendationsFlatland')
                            }
                          </span>
                        </div>
                        <span className="text-[18px] font-black" style={{ color: indexColor(rec.score / 10) }} data-tip="Eignungsbewertung 0-100 basierend auf Thermik, Distanz, Wind und Regen">
                          {rec.score}
                        </span>
                      </div>

                      {/* Field name */}
                      <h4 className="text-[13px] font-black text-[var(--text)] mb-2 line-clamp-2 leading-tight">
                        {rec.name}
                      </h4>

                      {/* Stats */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">
                            {t('weather.recommendationsDistance')}
                          </span>
                          <span className="text-[11px] font-black text-[var(--text)]">
                            {rec.distanceKm} km
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">
                            {rec.terrain === 'slope'
                              ? t('weather.recommendationsSlopeIndex')
                              : t('weather.recommendationsThermalIndex')
                            }
                          </span>
                          <span className="text-[11px] font-black"
                            style={{ color: indexColor(rec.terrain === 'slope' ? rec.slopeIndex : rec.thermalIndex) }}>
                            {(rec.terrain === 'slope' ? rec.slopeIndex : rec.thermalIndex).toFixed(1)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">
                            {t('weather.recommendationsWindInfo')}
                          </span>
                          <span className="text-[11px] font-black text-[var(--text)] flex items-center gap-1">
                            {rec.windSpeed.toFixed(0)} km/h
                            <span className="text-[10px] font-black text-[var(--accent)]" data-tip={`${rec.windDirection}°`}>
                              {degToCompass(rec.windDirection)}
                            </span>
                          </span>
                        </div>
                      </div>

                      {/* Wind Direction Match Badge (slope sites only) */}
                      {rec.terrain === 'slope' && rec.windMatch && (
                        <div className="mt-2 flex items-center gap-1.5">
                          {/* Compass indicator: outer = preferred dir, inner arrow = current wind */}
                          <div className="relative w-6 h-6 shrink-0" data-tip={
                            rec.preferred_wind_dir != null
                              ? `Bevorzugt: ${rec.preferred_wind_dir}° · Aktuell: ${rec.windDirection}° · Abweichung: ${rec.windMatchAngle ?? '?'}°`
                              : ''
                          }>
                            <svg width="24" height="24" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" fill="none" stroke="var(--border)" strokeWidth="1.5" />
                              {rec.preferred_wind_dir != null && (
                                <line x1="12" y1="12"
                                  x2={12 + 8 * Math.sin(rec.preferred_wind_dir * Math.PI / 180)}
                                  y2={12 - 8 * Math.cos(rec.preferred_wind_dir * Math.PI / 180)}
                                  stroke={rec.windMatch === 'perfect' ? '#22c55e' : rec.windMatch === 'good' ? '#84cc16' : '#ef4444'}
                                  strokeWidth="2" strokeLinecap="round" opacity="0.4" />
                              )}
                              <path d="M12 4L14 10H10L12 4Z"
                                fill={rec.windMatch === 'perfect' ? '#22c55e' : rec.windMatch === 'good' ? '#84cc16' : rec.windMatch === 'opposite' ? '#ef4444' : '#f97316'}
                                transform={`rotate(${rec.windDirection}, 12, 12)`} />
                            </svg>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${
                            rec.windMatch === 'perfect' ? 'bg-green-500/15 text-green-400' :
                            rec.windMatch === 'good' ? 'bg-lime-500/15 text-lime-400' :
                            rec.windMatch === 'opposite' ? 'bg-red-500/15 text-red-400' :
                            'bg-orange-500/15 text-orange-400'
                          }`}>
                            {rec.windMatch === 'perfect' ? t('weather.windMatchPerfect') :
                             rec.windMatch === 'good' ? t('weather.windMatchGood') :
                             rec.windMatch === 'opposite' ? t('weather.windMatchOpposite') :
                             t('weather.windMatchPoor')}
                          </span>
                        </div>
                      )}

                      {/* Reason */}
                      <p className="text-[10px] text-[var(--muted)] font-medium mt-2 leading-relaxed">
                        {rec.reason}
                      </p>

                      {/* r19.55.24: Zwei Links — 2D-Karte (Fluggelände) + 3D-Thermik-Atlas Deep-Link */}
                      <div className="mt-3 flex flex-col gap-1">
                        <a
                          href={`/flying-fields#${encodeURIComponent(rec.name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[var(--accent)] hover:underline cursor-pointer"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                          </svg>
                          {t('weather.recommendationsShowOnMap')}
                        </a>
                        <a
                          href={`/app/thermal-atlas?lat=${rec.lat.toFixed(4)}&lon=${rec.lon.toFixed(4)}&focus=hotspot`}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[var(--accent)] hover:underline cursor-pointer"
                          data-tip="Im Thermik-Atlas schräg heranzoomen"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v6m0 0l3-3m-3 3L9 5" /><path d="M4.22 13.5A7 7 0 1 0 12 6" />
                          </svg>
                          Im Thermik-Atlas ansehen
                        </a>
                      </div>
                    </div>
                  ))}
                    </div>
                  </>
                )
              })()}
            </div>

            {/* ══════ Thermik-Hotspots in der Naehe ══════ */}
            <div className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-[14px] font-black tracking-[.06em] uppercase text-[var(--text)]">
                    {t('thermal.nearbyHotspots')}
                  </h3>
                  <p className="text-[11px] text-[var(--muted)] font-medium mt-1">
                    {t('thermal.activeTodaySubtitle')}
                  </p>
                </div>
                {thermalForecast && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--pill-bg)]">
                    <span className="text-[10px] font-black tracking-[.06em] uppercase text-[var(--muted)]">{t('thermal.bestWindow')}</span>
                    <span className="text-[12px] font-black text-[var(--accent)]">{thermalForecast.bestTimeWindow}</span>
                  </div>
                )}
              </div>

              {thermalForecastLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-3 border-[var(--accent)]/30 border-t-[var(--accent)] rounded-full animate-spin" />
                  <span className="ms-3 text-[12px] font-bold text-[var(--muted)]">{t('thermal.todayForecast')}...</span>
                </div>
              )}

              {!thermalForecastLoading && (!thermalForecast || thermalForecast.hotspots.length === 0) && (
                <div className="text-center py-8">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 opacity-50">
                    <path d="M12 2v6m0 0l3-3m-3 3L9 5" /><path d="M4.22 13.5A7 7 0 1 0 12 6" />
                  </svg>
                  <p className="text-[13px] text-[var(--muted)] font-medium">{t('thermal.noHotspotsNearby')}</p>
                </div>
              )}

              {!thermalForecastLoading && thermalForecast && thermalForecast.hotspots.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {thermalForecast.hotspots.slice(0, 5).map((hs, i) => (
                    <a
                      key={hs.id}
                      href={`/app/thermal-atlas?hotspotId=${encodeURIComponent(hs.id)}&lat=${hs.lat.toFixed(4)}&lon=${hs.lon.toFixed(4)}&focus=hotspot`}
                      className="weather-card block rounded-[14px] border border-[var(--border)] p-4 hover:border-[var(--accent)]/40 hover:shadow-lg transition-all cursor-pointer no-underline"
                      style={{ borderLeftWidth: '3px', borderLeftColor: probColor(hs.todayProbability) }}
                      data-tip="Im Thermik-Atlas anzeigen — schräger Blick auf das Gebiet"
                    >
                      {/* Header with rank + probability */}
                      <div className="flex items-start justify-between mb-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black text-white" style={{ background: probColor(hs.todayProbability) }}>
                          {i + 1}
                        </span>
                        <span className="text-[20px] font-black" style={{ color: probColor(hs.todayProbability) }}>
                          {Math.round(hs.todayProbability * 100)}%
                        </span>
                      </div>

                      {/* Stats */}
                      <div className="space-y-1.5 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">{t('thermal.climbRate')}</span>
                          <span className="text-[11px] font-black text-[var(--text)]">{hs.avg_climb_rate.toFixed(1)} m/s</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">{t('thermal.altitudeGain')}</span>
                          <span className="text-[11px] font-black text-[var(--text)]">{Math.round(hs.avg_alt_gain)} m</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[var(--muted)]">{t('thermal.observations')}</span>
                          <span className="text-[11px] font-black text-[var(--text)]">{hs.score}x</span>
                        </div>
                      </div>

                      {/* Boost badges */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {hs.weatherBoost && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-green-500/10 text-[8px] font-bold text-green-400">
                            {t('thermal.weatherBoost')}
                          </span>
                        )}
                        {hs.windMatch && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-500/10 text-[8px] font-bold text-blue-400">
                            {t('thermal.windMatch')}
                          </span>
                        )}
                      </div>

                      {/* Reason */}
                      <p className="text-[9px] text-[var(--muted)] font-medium leading-relaxed line-clamp-2">
                        {hs.reason}
                      </p>

                      {/* r19.55.24: Deep-Link-Hinweis (r19.55.25: Beschriftung präzisiert) */}
                      <div className="mt-3 flex items-center gap-1 text-[10px] font-bold text-[var(--accent)]">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span>Im Thermik-Atlas ansehen</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {/* Link to full thermal atlas */}
              {thermalForecast && thermalForecast.hotspots.length > 0 && (
                <div className="text-center mt-4">
                  <a
                    href="/app/thermal-atlas"
                    className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[var(--accent)] hover:underline"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v6m0 0l3-3m-3 3L9 5" /><path d="M4.22 13.5A7 7 0 1 0 12 6" />
                    </svg>
                    {t('thermal.showOnAtlas')}
                  </a>
                </div>
              )}
            </div>

            </>}

            {/* ══════ Attribution Footer ══════ */}
            <div className="lg:col-span-3 rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-[11px] text-[var(--muted)] font-medium">
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <span>{t('weather.source')}: <strong>{weather.source || 'MET Norway + DWD'}</strong></span>
                </div>
                <span className="hidden sm:inline text-[var(--border)]">|</span>
                <span>{t('weather.api')}: DWD (DACH) · NOAA (USA) · JMA (Japan) · MET Norway / Yr.no (welt)</span>
                <span className="hidden sm:inline text-[var(--border)]">|</span>
                <span>{t('weather.license')}: NLOD 2.0 · GeoNutzV 2017 · 17 USC § 105 · GoJ Terms 2.0</span>
              </div>
              <p className="text-[10px] text-[var(--muted)] mt-2 opacity-70">
                {t('weather.disclaimer')}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
